import React, { useCallback, useEffect, useState } from 'react';
import { listProcoreFolder } from '../services/api';
import { Loading, ErrorText, errorMessage } from './ui';

// Walking a Procore project's Documents tree, for the two things that need it:
// choosing files to import, and choosing a folder to send one back to.
//
// Browsing is free - the GET side of the endpoint downloads, extracts and
// indexes nothing - so opening folders to look costs a Procore API call and no
// more. Only the caller's own action does work.
//
// What differs between the two uses is what happens to the FILES in a folder,
// so that is the only thing passed in. Everything else - the trail, the
// breadcrumb, loading, errors, the empty state - is the same browser, which is
// why it is one component rather than two that drift.
export default function ProcoreFolderBrowser({ projectId, rootName, renderFile, onFolderChange }) {
  // Each entry is { id, name }; the first is the project root, which Procore
  // identifies by an empty folder id.
  const [trail, setTrail] = useState([]);
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const openFolder = useCallback(async (id, name, nextTrail) => {
    setLoading(true);
    setError('');
    try {
      const data = await listProcoreFolder({ projectId, folderId: id });
      setFolder(data?.folder || { folders: [], files: [] });
      setTrail(nextTrail);
      onFolderChange?.({ id, name, trail: nextTrail });
    } catch (err) {
      setError(errorMessage(err));
      setFolder(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, onFolderChange]);

  useEffect(() => {
    const name = rootName || 'Documents';
    openFolder('', name, [{ id: '', name }]);
    // Opening the root once, when the browser appears. Re-running on every
    // change of openFolder would refetch the root each time the parent
    // re-renders and throw away where the person had navigated to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading) return <Loading text="Reading Procore…" />;
  if (error) return <ErrorText>{error}</ErrorText>;
  if (!folder) return null;

  return (
    <div className="procore-picker">
      {/* The breadcrumb doubles as the way back up: clicking a level reopens
          it, which is fewer controls than a separate Up button and cannot get
          out of step with where the view actually is. */}
      <p className="muted" style={{ marginBottom: 8 }}>
        {trail.map((step, i) => (
          <span key={`${step.id}-${i}`}>
            {i > 0 ? ' / ' : ''}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); openFolder(step.id, step.name, trail.slice(0, i + 1)); }}
            >
              {step.name}
            </a>
          </span>
        ))}
      </p>

      {(folder.folders || []).map((f) => (
        <div key={f.id} className="procore-option">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); openFolder(f.id, f.name, [...trail, { id: f.id, name: f.name }]); }}
          >
            📁 {f.name}
          </a>
        </div>
      ))}

      {renderFile ? (folder.files || []).map((file) => renderFile(file)) : null}

      {!(folder.folders || []).length && !(renderFile ? (folder.files || []).length : 0) ? (
        <p className="muted">
          {renderFile ? 'This folder is empty.' : 'No sub-folders here.'}
        </p>
      ) : null}
    </div>
  );
}
